import { useMemo, useState } from 'react';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { Select, SelectItem } from '@heroui/react';
import {
  ScormComparisonSide,
  useGetScormComparisonQuery,
  useGetScormDropOffQuery,
  useGetScormScoQuery,
} from '@/entities/Dashboard';
import { AppButton } from '@/shared/ui/AppButton';
import { ContainerTable } from '@/shared/ui/AppTable';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/common';
import {
  scormScoColumns,
  scormDropOffColumns,
  ScormScoTableRow,
  ScormDropOffTableRow,
} from '../../../config/scormColumns';
import { formatDurationOrDash, formatRateOrDash, formatScoreOrDash } from '../../../lib/formatDuration';
import { AnalyticsSectionHeader } from '../../components/AnalyticsSectionHeader';
import { resolveScormErrorMessage } from '../../../lib/resolveScormErrorMessage';
import styles from '../../dashboard/Dashboard.module.scss';

const PACKAGE_SOURCE_PAGE_SIZE = 100;

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="flex min-w-[150px] flex-1 flex-col gap-1 rounded-[8px] bg-[#F7F7F7] px-4 py-3">
    <span className="text-sm text-grey">{label}</span>
    <span className="text-xl font-bold text-black-headline">{value}</span>
  </div>
);

const SideColumn = ({ title, side }: { title: string; side: ScormComparisonSide }) => (
  <div className="flex flex-col gap-3 rounded-[8px] border border-[#E5E0EB] p-4">
    <h4 className="text-base font-semibold text-black-headline">{title}</h4>
    <div className="flex flex-wrap gap-3">
      <Metric label="Lessons" value={String(side.lessons)} />
      <Metric label="Learners" value={String(side.learners)} />
      <Metric label="Attempted" value={String(side.attempted)} />
      <Metric label="Completed" value={String(side.completed)} />
      <Metric label="Completion" value={formatRateOrDash(side.completionRate)} />
      <Metric label="Avg score" value={formatScoreOrDash(side.avgScore)} />
      <Metric label="Avg time" value={formatDurationOrDash(side.avgTimeSpentSeconds)} />
    </div>
  </div>
);

const ALL_PACKAGES = 'all';

export const ScormTab = () => {
  const [scoPackageId, setScoPackageId] = useState<number | null>(null);
  const [dropOffPackageId, setDropOffPackageId] = useState<number | null>(null);
  const [comparisonRequested, setComparisonRequested] = useState(false);
  const [scoPagination, setScoPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const { data: scoData, isFetching: isScoLoading, error: scoError } = useGetScormScoQuery({
    page: scoPagination.pageIndex + 1,
    pageSize: scoPagination.pageSize,
    ...(scoPackageId ? { packageId: scoPackageId } : {}),
  });

  const { data: packageSource } = useGetScormScoQuery({ page: 1, pageSize: PACKAGE_SOURCE_PAGE_SIZE });

  const packages = useMemo(() => {
    const byId = new Map<number, string>();
    (packageSource?.data.items ?? []).forEach((row) => {
      if (!byId.has(row.packageId)) byId.set(row.packageId, row.packageTitle);
    });
    return Array.from(byId, ([id, title]) => ({ id, title }));
  }, [packageSource]);

  const {
    data: dropOffData,
    isFetching: isDropOffLoading,
    error: dropOffError,
  } = useGetScormDropOffQuery(
    dropOffPackageId as number,
    { skip: !dropOffPackageId },
  );

  const {
    data: comparisonData,
    isFetching: isComparisonLoading,
    error: comparisonError,
  } = useGetScormComparisonQuery(
    undefined,
    { skip: !comparisonRequested },
  );

  const scoRows = useMemo<ScormScoTableRow[]>(
    () =>
      (scoData?.data.items ?? []).map((row) => ({
        ...row,
        id: `${row.packageId}-${row.scoIdentifier}`,
      })),
    [scoData],
  );

  const dropOffRows = useMemo<ScormDropOffTableRow[]>(
    () =>
      (dropOffData?.data.items ?? []).map((row) => ({
        ...row,
        id: `${row.orderIndex}-${row.scoIdentifier}`,
      })),
    [dropOffData],
  );

  const dropOff = dropOffData?.data;

  const scoErrorMessage = resolveScormErrorMessage(scoError);
  const dropOffErrorMessage = resolveScormErrorMessage(dropOffError);
  const comparisonErrorMessage = resolveScormErrorMessage(comparisonError);

  return (
    <div className={styles.root}>
      <section className={styles.section}>
        <AnalyticsSectionHeader title="SCORM by learning block" showToolbar={false} />
        <p className="text-sm text-grey-secondary">
          One row per learning block inside a package. Learners counts people, attempts counts
          attempts — one person may take a block several times. Figures are for all time.
        </p>

        <div className="mt-3 max-w-[320px]">
          <Select
            label="Package"
            labelPlacement="outside"
            selectedKeys={[scoPackageId ? String(scoPackageId) : ALL_PACKAGES]}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0];
              setScoPackageId(key && key !== ALL_PACKAGES ? Number(key) : null);
              setScoPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          >
            {[
              <SelectItem key={ALL_PACKAGES}>All packages</SelectItem>,
              ...packages.map((p) => <SelectItem key={String(p.id)}>{p.title}</SelectItem>),
            ]}
          </Select>
        </div>

        <div className="mt-3">
          {scoErrorMessage ? (
            <p className="py-10 text-center text-sm font-medium text-red-600">{scoErrorMessage}</p>
          ) : isScoLoading && !scoRows.length ? (
            <p className="py-10 text-center text-sm text-grey-secondary">Loading…</p>
          ) : scoRows.length ? (
            <ContainerTable<ScormScoTableRow>
              caption="SCORM by learning block"
              hideTopActions
              data={scoRows}
              columns={scormScoColumns}
              meta={scoData?.data.pagination}
              pagination={scoPagination}
              setPagination={setScoPagination as OnChangeFn<PaginationState>}
            />
          ) : (
            <p className="py-10 text-center text-sm text-grey-secondary">
              No SCORM attempts recorded yet.
            </p>
          )}
        </div>
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader title="Where learners stop" showToolbar={false} />
        <p className="text-sm text-grey-secondary">
          A funnel inside one package, not across a course. Pick a package to see it.
        </p>

        <div className="mt-3 max-w-[320px]">
          <Select
            label="Package"
            labelPlacement="outside"
            placeholder="Select a package"
            selectedKeys={dropOffPackageId ? [String(dropOffPackageId)] : []}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0];
              setDropOffPackageId(key ? Number(key) : null);
            }}
          >
            {packages.map((p) => (
              <SelectItem key={String(p.id)}>{p.title}</SelectItem>
            ))}
          </Select>
        </div>

        {!dropOffPackageId ? (
          <p className="mt-3 py-10 text-center text-sm text-grey-secondary">
            Select a package to see the funnel.
          </p>
        ) : dropOffErrorMessage ? (
          <p className="mt-3 py-10 text-center text-sm font-medium text-red-600">
            {dropOffErrorMessage}
          </p>
        ) : isDropOffLoading && !dropOff ? (
          <p className="mt-3 py-10 text-center text-sm text-grey-secondary">Loading…</p>
        ) : dropOff ? (
          <div className="mt-3 flex flex-col gap-3">
            <div className="flex flex-wrap gap-3">
              <Metric label="Learners" value={String(dropOff.learnersCount)} />
              <Metric label="Finished" value={String(dropOff.finishedCount)} />
              <Metric label="Blocks" value={String(dropOff.items.length)} />
            </div>

            {dropOff.biggestDropOff && (
              <div className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-700">
                  Biggest drop-off:{' '}
                  <span className="font-semibold">
                    {dropOff.biggestDropOff.title || dropOff.biggestDropOff.scoIdentifier}
                  </span>{' '}
                  — {dropOff.biggestDropOff.stoppedHereCount} learners stopped here (
                  {formatRateOrDash(dropOff.biggestDropOff.dropRate)})
                </p>
              </div>
            )}

            {!dropOff.orderFromManifest && (
              <p className="text-sm font-medium text-yellow-700">
                The package manifest lists no blocks — the order falls back to learner count, so the
                funnel is indicative only.
              </p>
            )}

            <p className="text-sm text-grey-secondary">
              Lesson: {dropOff.lessonTitle} · Package: {dropOff.packageTitle}
            </p>

            <ContainerTable<ScormDropOffTableRow>
              caption="Where learners stop"
              hideTopActions
              data={dropOffRows}
              columns={scormDropOffColumns}
            />
          </div>
        ) : (
          <p className="mt-3 py-10 text-center text-sm text-grey-secondary">
            This package has no recorded attempts.
          </p>
        )}
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader title="SCORM versus regular lessons" showToolbar={false} />
        <p className="text-sm text-grey-secondary">
          Both sides count learner-lesson pairs, not attempts. Time is SCORM-only: it is not recorded
          for regular lessons, so it shows a dash there. This report is the heaviest of the three, so
          it runs on demand.
        </p>

        <div className="mt-3">
          {!comparisonRequested ? (
            <AppButton color="black" onPress={() => setComparisonRequested(true)}>
              Load comparison
            </AppButton>
          ) : comparisonErrorMessage ? (
            <p className="py-10 text-center text-sm font-medium text-red-600">
              {comparisonErrorMessage}
            </p>
          ) : isComparisonLoading && !comparisonData ? (
            <p className="py-10 text-center text-sm text-grey-secondary">Loading…</p>
          ) : comparisonData ? (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <SideColumn title="SCORM" side={comparisonData.data.scorm} />
                <SideColumn title="Regular lessons" side={comparisonData.data.native} />
              </div>

              {comparisonData.data.byCourse.length > 0 && (
                <div className="overflow-x-auto rounded-[8px] border border-[#E5E0EB]">
                  <table className="w-full min-w-[760px] border-collapse">
                    <caption className="sr-only">SCORM compared with regular lessons</caption>
                    <thead>
                      <tr className="border-b border-[#E5E0EB] text-left">
                        <th scope="col" className="px-4 py-3 text-sm font-medium text-grey-secondary">Course</th>
                        <th scope="col" className="px-4 py-3 text-sm font-medium text-grey-secondary">
                          SCORM completion
                        </th>
                        <th scope="col" className="px-4 py-3 text-sm font-medium text-grey-secondary">
                          SCORM avg score
                        </th>
                        <th scope="col" className="px-4 py-3 text-sm font-medium text-grey-secondary">
                          Regular completion
                        </th>
                        <th scope="col" className="px-4 py-3 text-sm font-medium text-grey-secondary">
                          Regular avg score
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.data.byCourse.map((row) => (
                        <tr key={row.courseId} className="border-b border-[#F1F1F1] last:border-0">
                          <td className="px-4 py-3 text-base text-black-headline">
                            {row.courseTitle}
                          </td>
                          <td className="px-4 py-3 text-base text-black-headline">
                            {formatRateOrDash(row.scorm.completionRate)}
                          </td>
                          <td className="px-4 py-3 text-base text-black-headline">
                            {formatScoreOrDash(row.scorm.avgScore)}
                          </td>
                          <td className="px-4 py-3 text-base text-black-headline">
                            {formatRateOrDash(row.native.completionRate)}
                          </td>
                          <td className="px-4 py-3 text-base text-black-headline">
                            {formatScoreOrDash(row.native.avgScore)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <AppButton
                color="white"
                onPress={() => setComparisonRequested(false)}
                disabled={isComparisonLoading}
              >
                Hide
              </AppButton>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};
